"use client";
import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { SIFPortfolioData } from "@/type/sifPageTypes";

type Props = {
  portfolioData: SIFPortfolioData;
};

type AllocationItem = {
  name: string;
  value: number;
  color: string;
};

type SectorItem = {
  name: string;
  value: number;
};

type ChartWithLabel = Highcharts.Chart & {
  customLabel?: Highcharts.SVGElement;
};

const COLORS = ["#2AB625", "#4F46E5", "#F59E0B", "#EF4444", "#06B6D4", "#A855F7"];

export default function SIFInvestmentPortfolioStep({ portfolioData }: Props) {
  const [activeTab, setActiveTab] = useState<string>(portfolioData.tabList[0]?.name ?? "");

  const { allocationData, sectorData } = useMemo(() => {
    const activeKey = activeTab.toLowerCase().replace(/ /g, "_").replace(/&/g, "and");
    const current = portfolioData.resultArray[activeKey];

    if (!current) {
      return { allocationData: [], sectorData: [] };
    }

    // 🔥 MARKET CAP → PIE
    const allocation: AllocationItem[] = current.market_cap.map((item, index) => ({
      name: item.instrument,
      value: item.holdings,
      color: COLORS[index % COLORS.length],
    }));

    // 🔥 SECTOR → TOP 5
    const sector: SectorItem[] = [...current.sector]
      .sort((a, b) => b.holdings - a.holdings)
      .slice(0, 5)
      .map((item) => ({
        name: item.instrument,
        value: item.holdings,
      }));

    return {
      allocationData: allocation,
      sectorData: sector,
    };
  }, [portfolioData, activeTab]);

  const total = allocationData.reduce((acc, item) => acc + item.value, 0);

  const options = useMemo<Highcharts.Options>(() => {
    return {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        height: 150,
        events: {
          render() {
            const chart = this as ChartWithLabel;;
            const series = chart.series[0];
             if (!series.center) return;
            const centerY = series.center[1];
            const centerX = series.center[0];

            if (chart.customLabel) {
              chart.customLabel.destroy();
            }

            chart.customLabel = chart.renderer
              .text(`${total.toFixed(1)}%`, centerX, centerY)
              .css({
                color: "#fff",
                fontSize: "18px",
                fontWeight: "600",
                textAlign: "center",
              })
              .attr({
                align: "center",
              })
              .add();

            const bbox = chart.customLabel.getBBox();
            chart.customLabel.attr({
              x: centerX + bbox.width / 4,
              y: centerY + 8 + bbox.height / 4,
            });
          },
        },
      },
      title: {
        text: undefined,
      },
      credits: { enabled: false },
      tooltip: {
        enabled: true,
        backgroundColor: "#0f172a",
        borderWidth: 0,
        style: {
          color: "#fff",
          fontSize: "12px",
        },
        formatter: function () {
          const point = this.points ? this.points[0] : this;
          return `<b>${point.name}</b>: ${point.y}%`;
        },
      },
      legend: {
        enabled: true,
        align: "right",
        verticalAlign: "middle",
        layout: "vertical",
        itemStyle: {
          fontSize: "12px",
          fontWeight: "400",
        },
        itemMarginTop: 4,
        itemMarginBottom: 4,
        symbolRadius: 6,
        symbolHeight: 10,
        symbolWidth: 10,
        labelFormatter: function () {
          const item = this as Highcharts.Point;
          return `<span style="color:${item.color}">${item.name}</span>`;
        },
        useHTML: true,
      },
      plotOptions: {
        pie: {
          size: "90%",
          innerSize: "70%",
          borderWidth: 4, // 🔥 creates gap
          borderColor: "#0f172a", // same as background for clean separation
          dataLabels: { enabled: false },
          showInLegend: true,
          slicedOffset: 0,
          states: {
            hover: {
              enabled: true,
              brightness: 0.05,
            },
          },
        },
      },
      series: [
        {
          type: "pie",
          data: allocationData.map((item) => ({
            name: item.name,
            y: item.value,
            color: item.color,
          })),
        },
      ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 176,
            },
            chartOptions: {
              chart: {
                height: 270,
              },
              legend: {
                layout: "vertical",
                align: "center",
                verticalAlign: "bottom",
              },
            },
          },
        ],
      },
    };
  }, [allocationData, activeTab]);

  return (
    <div className="flex flex-col sm:flex-row items-stretch justify-between gap-4 sm:gap-6">
      {/* Asset Allocation */}
      <div className="w-full sm:w-fit bg-white/5 border border-white/15 rounded-2xl shrink-0 px-10 pt-4 pb-0">
        <div className="text-white font-bold mb-3">Asset Allocation</div>

        <div className="max-w-60 xxs:max-w-96 sm:max-w-44 mx-auto">
          <HighchartsReact highcharts={Highcharts} options={options} immutable={true} />
        </div>
      </div>

      {/* Sector Exposure */}
      <div className="w-full bg-white/5 border border-white/15 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-white font-bold">Top Sectors</div>

          <div className="flex gap-2">
            {portfolioData.tabList.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  activeTab === tab.name ? "bg-white text-black" : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-5">
          {sectorData.map((item) => (
            <div key={item.name}>
              <div className="flex justify-between text-white text-sm mb-1">
                <span className="text-[#D1D5DC]">{item.name}</span>
                <span className="font-semibold">{item.value.toFixed(2)}%</span>
              </div>

              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#2AB625] rounded-full" style={{ width: `${item.value}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
